import { FC, useEffect, useState, useCallback, useRef } from "react";
import CustomSelect from "../../../../../../../CommonComponents/CustomSelect";
import { showToastMessage } from "../../../../../../../../utils/commonFunctions";
import ValidateService from "../../../../../../../../service/ValidateService";
import type { Option, TestCaseConfig, TestPlanConfig } from "../../types/consoleTypes";
import useUrlParams from "../../../../../../hooks/useUrlParams";
import { deriveIsCritical } from "../../../../../../../Missions/Satellites/TestCase/TestCaseHelpers";
import { CriticalIcon, OnboardIcon } from "../../../../../../../Missions/Satellites/TestCase/svgs/TestCaseSvgs";
import CustomToolTip from "../../../../../../../CommonComponents/CustomToolTip";

const MODE_CONFIG = {
  "TEST PLAN": {
    fetch: (missionId: string, satelliteId: string) => ValidateService.getAllTestPlans(missionId, satelliteId),
    toOption: (item: any): Option<TestPlanConfig> => {
      const isCritical = !!item.isCritical;
      const isOnboard =
        item.executionLocation === 'ONBOARD' ||
        item.executionLocation === 'MIXED' ||
        Boolean(item.executeOnboard) ||
        item.testCaseDetails?.some((t: any) => t.executionLocation === 'ONBOARD' || Boolean(t.executeOnboard)) ||
        item.testCases?.some((tc: any) => tc.executionLocation === 'ONBOARD' || Boolean(tc.executeOnboard));
      const hasAnyRightIcon = isCritical || isOnboard;
      return {
        label: item.testPlanName ?? item.testPlanId ?? "Unnamed plan",
        value: item as TestPlanConfig,
        rightIcon: hasAnyRightIcon ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {isCritical && (
              <CustomToolTip title="Critical Plan" placement="top">
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <CriticalIcon />
                </span>
              </CustomToolTip>
            )}
            {isOnboard && (
              <CustomToolTip title={item.executionLocation === 'MIXED' ? "Mixed Execution (Ground & Onboard)" : "Onboard Execution"} placement="top">
                <span className="tcd-onboard-icon-cell" style={{ display: 'inline-flex', alignItems: 'center', margin: 0 }}>
                  <OnboardIcon />
                </span>
              </CustomToolTip>
            )}
          </div>
        ) : undefined,
      };
    },
    getId: (item: any): string | undefined => item?.testPlanId,
  },
  TEST: {
    fetch: (missionId: string, satelliteId: string) => ValidateService.getAllTestCases(missionId, satelliteId),
    toOption: (item: any): Option<TestCaseConfig> => {
      const isCritical = deriveIsCritical(item);
      const isOnboard = Boolean(item.executeOnboard) || item.executionLocation === 'ONBOARD';
      const hasAnyRightIcon = isCritical || isOnboard;
      return {
        label: item?.testCaseName ?? item?.name ?? item?.testCaseId ?? "Unnamed test case",
        value: {
          testCaseId: item?.testCaseId,
          testPlanId: item?.testPlanId,
          version: item?.version,
          createdAt: item?.createdAt,
          updatedAt: item?.updatedAt,
          testCaseDetails: item?.testCaseDetails ?? [],
          isCritical: isCritical,
          executeOnboard: isOnboard,
          executionLocation: item?.executionLocation,
        } as TestCaseConfig,
        rightIcon: hasAnyRightIcon ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {isCritical && (
              <CustomToolTip title="Critical Test Case" placement="top">
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <CriticalIcon />
                </span>
              </CustomToolTip>
            )}
            {isOnboard && (
              <CustomToolTip title="Onboard Execution" placement="top">
                <span className="tcd-onboard-icon-cell" style={{ display: 'inline-flex', alignItems: 'center', margin: 0 }}>
                  <OnboardIcon />
                </span>
              </CustomToolTip>
            )}
          </div>
        ) : undefined,
      };
    },
    getId: (item: any): string | undefined => item?.testCaseId,
  },
} as const;

const optionCache = new Map<string, Option<any>[]>();
const optionRequestCache = new Map<string, Promise<Option<any>[]>>();

const getOptionCacheKey = (mode: "TEST" | "TEST PLAN", missionId: string, satelliteId: string) =>
  `${mode}:${missionId}:${satelliteId}`;

interface TestDropdownProps {
  mode: "TEST" | "TEST PLAN";
  satelliteId: string;
  value: Option<TestCaseConfig> | Option<TestPlanConfig> | null;
  onChange: (opt: Option<TestCaseConfig> | Option<TestPlanConfig> | null) => void;
  isResolvingSelection?: boolean;
}

const TestDropdown: FC<TestDropdownProps> = ({ mode, satelliteId, value, onChange, isResolvingSelection = false }) => {
  const { mission_id } = useUrlParams();
  const [options, setOptions] = useState<Option<any>[]>([]);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);
  const valueRef = useRef(value);
  useEffect(() => { valueRef.current = value; }, [value]);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const fetchOptions = useCallback(async () => {
    if (!mission_id || !satelliteId) {
      setOptions([]);
      setLoading(false);
      onChangeRef.current(null);
      return;
    }
    const requestId = ++requestIdRef.current;
    const config = MODE_CONFIG[mode];
    const shouldCacheOptions = mode === "TEST";
    const cacheKey = getOptionCacheKey(mode, mission_id, satelliteId);
    const cachedOptions = shouldCacheOptions ? optionCache.get(cacheKey) : undefined;
    if (cachedOptions) {
      setOptions(cachedOptions);
      setLoading(false);
      const currentId = config.getId(valueRef.current?.value);
      if (currentId && !cachedOptions.some((option) => config.getId(option.value) === currentId)) {
        onChangeRef.current(null);
      }
      return;
    }

    setLoading(true);
    try {
      let newOptions: Option<any>[];
      if (shouldCacheOptions) {
        const request = optionRequestCache.get(cacheKey) ?? config.fetch(mission_id, satelliteId).then((res) => {
          const payload = res?.data?.data ?? res?.data;
          const items: any[] = Array.isArray(payload) ? payload : [];
          return items
            .filter((item) => Boolean(String(config.getId(item) ?? "").trim()))
            .map((item) => config.toOption(item));
        });
        optionRequestCache.set(cacheKey, request);
        newOptions = await request;
      } else {
        const res = await config.fetch(mission_id, satelliteId);
        const payload = res?.data?.data ?? res?.data;
        const items: any[] = Array.isArray(payload) ? payload : [];
        newOptions = items
          .filter((item) => Boolean(String(config.getId(item) ?? "").trim()))
          .map((item) => config.toOption(item));
      }

      if (requestId !== requestIdRef.current) return;
      if (shouldCacheOptions) optionCache.set(cacheKey, newOptions);
      setOptions(newOptions);
      const currentId = config.getId(valueRef.current?.value);
      if (currentId && !newOptions.some((o) => config.getId(o.value) === currentId)) {
        onChangeRef.current(null);
      }
    } catch {
      if (requestId !== requestIdRef.current) return;
      showToastMessage("error", `Failed to load ${mode === "TEST PLAN" ? "test plans" : "test cases"}`);
      setOptions([]);
    } finally {
      if (shouldCacheOptions) optionRequestCache.delete(cacheKey);
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [mission_id, satelliteId, mode]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);
  useEffect(() => () => { requestIdRef.current += 1; }, []);

  const label = mode === "TEST PLAN" ? "Test Plan" : "Test Case";
  const placeholder = loading
    ? "Loading…"
    : isResolvingSelection
      ? "Loading test plan details…"
      : `Select ${mode === "TEST PLAN" ? "test plan" : "test case"}`;

  return (
    <div
      className="left__panel__body__input__dropdown__container left__panel__body__input__custom__dropdown left__panel__body_input__border"
      data-testid={mode === "TEST PLAN" ? "console-testplan-dropdown" : "console-testcase-dropdown"}
    >
      <span className="input__label">{label}</span>
      <CustomSelect
        options={options}
        value={value}
        placeholder={placeholder}
        onChange={(opt) => onChange(opt as Option<TestCaseConfig> | Option<TestPlanConfig> | null)}
        background="rgba(10,10,10,0.65)"
        isSearchable
        isDisabled={loading || isResolvingSelection}
      />
    </div>
  );
};

export default TestDropdown;
